import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Container,
    Paper,
    CircularProgress,
} from "@mui/material";
import FMBlogo from "../../../assets/FMBlogo1.png";
import { CLIENT_ID, LOGIN_HUB_URL } from "../../../config";

const Index = ({ campaignCode }) => {
    const location = useLocation();
    const [error, setError] = useState("");
    const [loginLoading, setLoginLoading] = useState(false);

    // Note: This component is only shown when user is NOT logged in
    // The CampaignEntry component handles logged-in users before showing this page

    useEffect(() => {
        if (location.state?.error) {
            setError(location.state.error);
        } else {
            setError("");
        }
    }, [location]);

    const handleLogin = async () => {
        setLoginLoading(true);
        try {
            //const baseURL = "http://customer-api.secureivs.eu/";
            // const clientId = "qMpkpVQPBadSLwmREGRbu2lsoqCzeZ"; // live
            //  const clientId = "VbQu8HRymrbHktZ9HPUH97R5DGoubf"; // dev
            const loginMethod = "sms";
            const redirectURI = `${window.location.origin}/verify-mobile-register?campaignCode=${campaignCode}`;

            const loginURL = `${LOGIN_HUB_URL}?client_id=${CLIENT_ID}&login_method=${loginMethod}&redirect_uri=${encodeURIComponent(
                redirectURI
            )}`;
            window.location.href = loginURL;
        } catch (err) {
            setLoginLoading(false);
            setError("Kunne ikke starte registrering. Vennligst prøv igjen.");
        }
    };

    useEffect(() => {
        const isMobile = () => /Mobi|Android/i.test(navigator.userAgent);

        const getContainerWidth = (iframe) => {
            const container = iframe.parentElement;
            return container?.clientWidth || iframe.offsetWidth || window.innerWidth;
        };

        const getDesktopHeight = (iframe) => {
            const w = getContainerWidth(iframe);
            return Math.max(Math.round(w * (9 / 16)), 280);
        };

        const updateIframeDimensions = () => {
            const iframe = document.getElementById("content-iframe");
            if (!iframe) return;
            if (isMobile()) {
                iframe.style.height = "60vh";
            } else {
                iframe.style.height = `${getDesktopHeight(iframe)}px`;
            }
        };

        const handleMessage = (event) => {
            if (event.origin !== "https://admin.easyque.eu") return;

            const iframe = document.getElementById("content-iframe");
            if (event.data.height && iframe) {
                if (isMobile()) {
                    iframe.style.height = "60vh";
                } else {
                    const receivedHeight = parseInt(event.data.height, 10);
                    const aspectHeight = getDesktopHeight(iframe);
                    const maxHeight = Math.min(
                        receivedHeight,
                        window.innerHeight * 0.55
                    );
                    iframe.style.height = `${Math.max(maxHeight, aspectHeight)}px`;
                }
            }
        };

        const setIframeSrc = () => {
            const iframe = document.getElementById("content-iframe");
            if (iframe) {
                iframe.src = isMobile()
                    ? "https://admin.easyque.eu/home/display_ad_Carousel_Portrait?branch_id=1"
                    : "https://admin.easyque.eu/home/display_ad_Carousel_landscape?branch_id=1";
                updateIframeDimensions();
            }
        };

        const handleResize = () => updateIframeDimensions();

        window.addEventListener("message", handleMessage);
        window.addEventListener("resize", handleResize);
        setIframeSrc();

        return () => {
            window.removeEventListener("message", handleMessage);
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    return (
        <div>
            <Container maxWidth="sm" sx={{ py: 4 }}>
                <Box textAlign="center" mb={0}>
                    <img src={FMBlogo} alt="Fargerike Logo" width="230" />
                </Box>

                {error && (
                    <Box
                        sx={{
                            bgcolor: "error.light",
                            border: "1px solid",
                            borderColor: "error.main",
                            borderRadius: 2,
                            p: 2,
                            mb: 3,
                            textAlign: "center",
                            opacity: 0.95,
                        }}
                    >
                        <Typography
                            variant="body1"
                            color="error.dark"
                            fontWeight="medium"
                            sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
                        >
                            {error}
                        </Typography>
                    </Box>
                )}

                <Typography variant="body1" textAlign="center" mb={3}>
                    Tast inn ditt mobilnummer så får du tilgang til dine kuponger.
                </Typography>

                <Box textAlign="center" mb={4}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleLogin}
                        disabled={loginLoading}
                        startIcon={
                            loginLoading ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : null
                        }
                        sx={{ minWidth: 200, py: 1.5 }}
                    >
                        {loginLoading
                            ? "Starter registrering..."
                            : "REGISTRERING / LOGG INN"}
                    </Button>
                </Box>
            </Container>

            <Container maxWidth="md" sx={{ pb: 2, px: { xs: 2, sm: 3 } }}>
                <Card sx={{ position: "relative", overflow: "hidden" }}>
                    <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                        <Box
                            className="iframe-container"
                            sx={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "center",
                                minHeight: { xs: "200px", sm: "280px" },
                            }}
                        >
                            <iframe
                                id="content-iframe"
                                title="Butikkannonse"
                                scrolling="no"
                                style={{
                                    border: "none",
                                    width: "100%",
                                    minHeight: "200px",
                                    display: "block",
                                }}
                            ></iframe>
                        </Box>
                    </CardContent>
                </Card>
            </Container>

            <Container maxWidth="sm" sx={{ pb: 4 }}>
                <Typography variant="body2">
                    Dette er kun en tjeneste for kunder av Fargerike Majorstuen
                </Typography>
                <Paper>
                    <Box mt={5} textAlign="center">
                        <img src={FMBlogo} alt="Fargerike Logo" width="230" />
                        <Typography variant="body2" color="text.secondary">
                            Sørkedalsveien 10
                            <br />
                            Colosseum Senter
                            <br />
                            Email: butikk@fargerikemajorstuen.no
                            <br />
                            Åpningstider:
                            <br />
                            Mandag – Onsdag 06:30 – 18:00
                            <br />
                            Torsdag – Fredag 06:30 – 19:00
                            <br />
                            Lørdag 08:00 – 16:00
                        </Typography>
                        <br />
                    </Box>
                </Paper>
            </Container>
        </div>
    );
};

export default Index;
